import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

/**
 * TanStack Query hooks for store settings and import operations
 */

// API response types
export interface StoreSettings {
  wooUrl: string | null
  wooKeySet: boolean
  wooSecretSet: boolean
  widgetPrimaryColor: string
  widgetMode: 'dropdown' | 'fullscreen'
  lastSyncAt: number | null
  productCount: number
  categoryCount: number
}

interface UpdateSettingsInput {
  wooUrl?: string
  wooKey?: string
  wooSecret?: string
  widgetPrimaryColor?: string
  widgetMode?: 'dropdown' | 'fullscreen'
}

interface UpdateSettingsResponse {
  success: boolean
  settings: StoreSettings
}

interface TestConnectionInput {
  wooUrl?: string
  wooKey?: string
  wooSecret?: string
}

interface TestConnectionResponse {
  success: boolean
  productCount?: number
  message?: string
  error?: string
}

interface ImportResponse {
  status: string
  message: string
}

export interface ImportStatus {
  // 'running' is the new workflow status; 'fetching'/'indexing' kept for legacy compat
  status: 'idle' | 'running' | 'fetching' | 'indexing' | 'complete' | 'error'
  progress: number
  total: number
  message: string
  workflowId?: string | null
  liveWorkflowStatus?: string | null
  error?: string | null
  startedAt?: number | string | null
  lastSync?: {
    completedAt: number
    productsImported: number
  } | null
}

/**
 * Fetch store settings
 */
export function useStoreSettings() {
  return useQuery({
    queryKey: ['store-settings'],
    queryFn: async (): Promise<StoreSettings> => {
      const response = await fetch('/api/settings', {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch settings')
      }

      return response.json()
    },
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Update store settings
 */
export function useUpdateStoreSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UpdateSettingsInput): Promise<UpdateSettingsResponse> => {
      const response = await fetch('/api/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      })

      if (!response.ok) {
        const errorData: { error?: string } = await response.json()
        throw new Error(errorData.error || 'Failed to update settings')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-settings'] })
    },
  })
}

/**
 * Test WooCommerce connection
 */
export function useTestConnection() {
  return useMutation({
    mutationFn: async (input?: TestConnectionInput): Promise<TestConnectionResponse> => {
      const response = await fetch('/api/settings/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input || {}),
      })

      const data: TestConnectionResponse = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Connection test failed')
      }

      return data
    },
  })
}

/**
 * Trigger product import
 */
export function useImport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (clearExisting?: boolean): Promise<ImportResponse> => {
      const response = await fetch('/api/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clearExisting: clearExisting ?? true }),
      })

      if (!response.ok) {
        const errorData: { error?: string } = await response.json()
        throw new Error(errorData.error || 'Failed to start import')
      }

      return response.json()
    },
    onSuccess: () => {
      // Invalidate both settings and import status
      queryClient.invalidateQueries({ queryKey: ['store-settings'] })
      queryClient.invalidateQueries({ queryKey: ['import-status'] })
    },
  })
}

/**
 * Poll import status
 */
export function useImportStatus(_enabled = false) {
  return useQuery({
    queryKey: ['import-status'],
    queryFn: async (): Promise<ImportStatus> => {
      const response = await fetch('/api/import/status', {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch import status')
      }

      return response.json()
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status
      // Poll every 2s while a workflow is running. 'running' is the
      // current workflow status; the older 'fetching'/'indexing' values
      // stay supported in case any in-flight syncs use them.
      if (status === 'running' || status === 'fetching' || status === 'indexing') {
        return 2000
      }
      return false
    },
    // Always enabled — we want to detect a running workflow on page load
    // even if the user didn't click Sync this session (e.g., a cron sync
    // or a sync started in another tab).
    enabled: true,
    staleTime: 0,
  })
}
